/**
 * Global Mixin: Submit on Enter Key Press
 * 
 * Automatically handles Enter key press to submit/confirm actions in modals, dialogs, and forms.
 * 
 * How it works:
 * 1. Tracks common modal/dialog states (dialog, isDisplay, popup, etc.)
 * 2. Tracks common submit methods (onSubmit, onSave, apply, confirm, etc.)
 * 3. When Enter is pressed and a modal is open, calls the appropriate submit method
 * 4. Prevents submission when typing in textarea or when Ctrl/Cmd/Alt keys are pressed
 * 
 * Usage:
 * - Register globally in main.js: Vue.mixin(submitOnEnterMixin)
 * - Components should define submit methods (onSubmit, onSave, apply, confirm, etc.)
 * - The mixin will automatically detect and call the appropriate method
 * 
 * Supported modal states:
 * - dialog, isDisplay, popup, active, isConfig, isCustomP2P98, etc.
 * 
 * Supported submit methods:
 * - onSubmit, onSave, apply, confirm, save, submit, onCreate, onUpdate, onConfirm
 */

export default {
  data() {
    return {
      _enterHandlerAttached: false,
      _enterWatchers: []
    };
  },

  mounted() {
    // Initialize mixin data if not already done
    if (!this._enterWatchers) {
      this._enterWatchers = [];
    }
    if (typeof this._enterHandlerAttached === 'undefined') {
      this._enterHandlerAttached = false;
    }

    // Check if this component has any modal states or submit methods
    if (!this._hasSubmitCapability()) {
      return;
    }

    // Setup watchers for all modal states
    this._setupEnterWatchers();

    // Check if any modal is already open on mount
    this.$nextTick(() => {
      if (this._isAnyModalOpen() && !this._enterHandlerAttached) {
        this._attachEnterHandler();
      }
    });
  },

  beforeDestroy() {
    // Clean up event listener
    this._detachEnterHandler();

    // Clean up all watchers
    if (this._enterWatchers && this._enterWatchers.length > 0) {
      this._enterWatchers.forEach(unwatch => unwatch());
      this._enterWatchers = [];
    }
  },

  methods: {
    /**
     * Check if component has submit capability
     * (has modal states and submit methods)
     */
    _hasSubmitCapability() {
      const modalStates = this._getModalStates();
      const submitMethods = this._getSubmitMethods();
      
      return modalStates.length > 0 && submitMethods.length > 0;
    },

    /**
     * Get list of modal state properties in this component
     */
    _getModalStates() {
      const commonModalStates = [
        'dialog',
        'isDisplay',
        'popup',
        'active',
        'isConfig',
        'isCustomP2P98',
        'rename_dialog',
        'dialogStyle',
        'showEditOption',
        'isShowDialog',
        'showEditDialog',
        'isSave',
        'popupProperty',
        'dialogTypeResult',
        'dialogRun',
        'dialogClassify',
        'dialogUpload',
        'isCopy',
        'isRemoveAOI',
        'showDetail',
        'isMakeCopy',
        'info',
        'isRename',
        'dialogProperties'
      ];

      return commonModalStates.filter(state => state in this.$data);
    },

    /**
     * Get list of submit methods available in this component
     */
    _getSubmitMethods() {
      const commonSubmitMethods = [
        'onSubmit',
        'onSave',
        'apply',
        'confirm',
        'save',
        'submit',
        'onCreate',
        'onUpdate',
        'onConfirm',
        'handleSubmit',
        'handleSave',
        'handleConfirm',
        'createNewAOI',
        'editAOI',
        'updateGeomAOI',
        'saveModel',
        'runClassification',
        'runDetection',
        'updateImage',
        'createLayer'
      ];

      const availableMethods = commonSubmitMethods.filter(method => 
        typeof this[method] === 'function'
      );

      // Prioritize specific methods for nested dialogs
      // If dialogProperties is open, only use createLayer (ignore submit)
      if (this.dialogProperties && availableMethods.includes('createLayer')) {
        return ['createLayer'];
      }

      return availableMethods;
    },

    /**
     * Check if any modal is currently open
     */
    _isAnyModalOpen() {
      const modalStates = this._getModalStates();
      return modalStates.some(state => this[state] === true);
    },

    /**
     * Setup watchers for all modal states
     */
    _setupEnterWatchers() {
      const modalStates = this._getModalStates();

      modalStates.forEach(stateName => {
        const unwatch = this.$watch(stateName, (newVal) => {
          if (newVal === true) {
            // Modal opened - attach Enter handler
            if (!this._enterHandlerAttached) {
              this._attachEnterHandler();
            }
          } else {
            // Modal closed - check if all modals are closed
            if (!this._isAnyModalOpen() && this._enterHandlerAttached) {
              this._detachEnterHandler();
            }
          }
        });

        this._enterWatchers.push(unwatch);
      });
    },

    /**
     * Attach Enter key event listener
     */
    _attachEnterHandler() {
      if (this._enterHandlerAttached) return;

      document.addEventListener('keydown', this._handleEnterKey);
      this._enterHandlerAttached = true;

      // Track this component as having an active Enter handler
      if (!window._componentsWithEnterHandler) {
        window._componentsWithEnterHandler = new Set();
      }
      window._componentsWithEnterHandler.add(this._uid);
    },

    /**
     * Detach Enter key event listener
     */
    _detachEnterHandler() {
      if (!this._enterHandlerAttached) return;

      document.removeEventListener('keydown', this._handleEnterKey);
      this._enterHandlerAttached = false;

      // Remove from active handlers tracking
      if (window._componentsWithEnterHandler) {
        window._componentsWithEnterHandler.delete(this._uid);
      }
    },

    /**
     * Handle Enter key press
     */
    _handleEnterKey(event) {
      // Only handle Enter key
      if (event.key !== 'Enter') return;

      // Skip if modal is not open
      if (!this._isAnyModalOpen()) return;

      // Skip if modifier keys are pressed (Ctrl+Enter, Cmd+Enter, Alt+Enter)
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      // Skip if user is typing in a textarea
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName === 'TEXTAREA') {
        return;
      }

      // Skip if user is in a contenteditable element
      if (activeElement && activeElement.isContentEditable) {
        return;
      }

      // Skip if inside a multi-line input or rich text editor
      if (activeElement && (
        activeElement.classList.contains('ql-editor') || // Quill editor
        activeElement.classList.contains('CodeMirror') || // CodeMirror
        activeElement.getAttribute('contenteditable') === 'true'
      )) {
        return;
      }

      // Skip if inside a v-combobox or v-select (Vuetify)
      // These components use Enter for selecting/adding items
      if (activeElement && (
        activeElement.getAttribute('role') === 'combobox' ||
        activeElement.closest('.v-select') ||
        activeElement.closest('.v-combobox') ||
        activeElement.closest('.v-autocomplete')
      )) {
        return;
      }

      // Only one component should handle the Enter key (the topmost modal)
      // Check if this component is the most recently mounted one with Enter handler
      if (window._componentsWithEnterHandler && window._componentsWithEnterHandler.size > 1) {
        const handlers = Array.from(window._componentsWithEnterHandler);
        const latestHandler = handlers[handlers.length - 1];
        if (this._uid !== latestHandler) {
          return; // Let the topmost modal handle it
        }
      }

      // Find and call the first available submit method
      const submitMethods = this._getSubmitMethods();
      if (submitMethods.length > 0) {
        const methodName = submitMethods[0];
        
        // Additional safety check before calling
        if (typeof this[methodName] !== 'function') {
          console.warn(`Method ${methodName} is not a function in component:`, this.$options.name || this.$options._componentTag);
          return;
        }
        
        // Prevent default form submission
        event.preventDefault();
        event.stopPropagation();

        // Call the submit method
        try {
          this[methodName]();
        } catch (error) {
          // Silently handle validation errors - let component's own validation handle it
          // Only log to console for debugging
          console.warn(`[Submit on Enter] Validation error in ${methodName}:`, error.message);
          
          // Show user-friendly error if snackbar is available
          if (this.$snackbar && typeof this.$snackbar.showError === 'function') {
            this.$snackbar.showError('Please complete all required fields');
          }
        }
      }
    }
  }
};



// WEBPACK FOOTER //
// ./src/mixins/submitOnEnterMixin.js